import { useNuxtApp } from 'nuxt/app';
import { hasProperty } from '.';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { email, between, helpers, minValue as minValueValidator, requiredIf } from '@vuelidate/validators';
import type { ValidationRule, BaseValidation, ValidationRuleCollection } from '@vuelidate/core';
import type { Ref } from 'vue';
import type { PostTranslationPluginProvides } from '~~/common/plugins/post-translation';

type NamedValidator = BaseValidation & { required?: ValidationRule };
interface BasicValidatorProps {
  field: {
    rowUniqueId: string | undefined
    hasError: boolean
    hasSuccess: boolean
    isInvalid: boolean
    isRequired: boolean
    onBlur: () => void
  }
  error: {
    id: string | undefined
    validator: NamedValidator | undefined
  }
}
interface CreateValidatorInput {
  basicValidatorProps?: BasicValidatorProps
  namedValidator?: NamedValidator
}
interface CreateValidatorContext {
  validator?: ValidationRuleCollection
  validatorName?: string
  model?: { value: unknown }
  id?: string
}
interface CreateValidatorResult {
  error: { id: string | undefined; validator: NamedValidator | undefined }
  field: Record<string, unknown>
}

type CreateValidatorFn = (
  input: CreateValidatorInput,
  context: CreateValidatorContext,
  ...rest: unknown[]
) => CreateValidatorResult;

export const phonePrefix = computed(() => {

  const userInterfaceStore = useUserInterfaceStore();
  const validator = userInterfaceStore.getValidator('phone_number');
  return Object.keys(validator?.metaData || {})?.[0];
});

export function awRequiredOptional (validatorFunction: ValidationRule) {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt('aw.common.form.validation.required_optional'),
    validatorFunction,
  );
}

export function awRequiredCheckbox () {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt('aw.common.form.validation.required'),
    (val: boolean) => val === true,
  );
}

export function awRequired ({ ifFun }: { ifFun?: () => boolean } = {}) {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt('aw.common.form.validation.required'),
    requiredIf(ifFun || (() => true)),
  );
}

export function awEmail () {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt('aw.common.form.validation.email'),
    email,
  );
}

export function awMinValue (value: number, token: string) {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt(token),
    minValueValidator(value),
  );
}

export function awBetween (minValue: number | Ref<number>, maxValue: number | Ref<number>, token: string) {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt(token),
    between(minValue, maxValue),
  );
}

export function awTajNumberValidator () {
  const userInterfaceStore = useUserInterfaceStore();
  const tajValidator = userInterfaceStore.tajValidator();

  return withMessageForInvalid(
    // - tajszám: 9 db szám karakter, szigorúan string -ként.
    tajValidator?.instruction || '',
    (val: string) => userInterfaceStore.tajValidate(val),
  );
}

export function awTaxNumberValidator () {
  const userInterfaceStore = useUserInterfaceStore();
  const taxIdValidator = userInterfaceStore.taxIdValidator();

  return withMessageForInvalid(
    // - adószám: egy nyolcas, és 9 db számjegy, szintén stringként.
    taxIdValidator?.instruction || '',
    (val: string) => userInterfaceStore.taxIdValidate(val),
  );
}

export function awPasswordConfirm (comparisonFunction: ValidationRule) {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt('aw.common.form.validation.same_password'),
    comparisonFunction,
  );
}

export function awValidOptionalMobile () {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt('aw.common.form.validation.phone_invalid'),
    (val: string) => {
      return (val
        ? useUserInterfaceStore().phoneValidator([
          phonePrefix?.value || '',
          val?.slice(0, 2) || '',
          val?.slice(2) || '',
        ])
        : true
      );
    },
  );
}

export function awIdNotEqualWith (id: number) {
  const { $awt } = useNuxtApp();
  return withMessageForInvalid(
    $awt('aw.common.form.validation.required'),
    (val: { id: number }) => Boolean(val?.id) && val.id !== id,
  );
}

export function createValidator (fun: CreateValidatorFn) {
  return function (context: CreateValidatorContext = {}, ...rest: unknown[]) {
    const {
      validator,
      validatorName,
      model,
      id,
    } = context;
    const namedValidator = validator?.[validatorName ?? ''] as NamedValidator | undefined;
    const { $logger } = useNuxtApp();
    if (typeof validator !== 'object') {
      $logger.error(new TypeError(`typeof validator should be object instead received ${typeof validator}`));
    }
    if (typeof namedValidator !== 'object') {
      $logger.error(new TypeError(`typeof namedValidator should be object instead received ${typeof namedValidator}`));
    }
    if (typeof validatorName !== 'string') {
      $logger.error(new TypeError(`typeof validatorName should be string or null instead received ${typeof validatorName}`));
    }
    if (model?.value === undefined && model !== null) {
      $logger.error(new TypeError(`typeof model.value should not be undefined or model should be null instead received a model which is ${typeof model}<${typeof model?.value}>`));
    }
    if (typeof id !== 'string' && id !== null) {
      $logger.error(new TypeError(`typeof id should be string or null instead received ${typeof id}`));
    }
    const result = fun(
      {
        namedValidator,
        basicValidatorProps: getBasicValidatorProps({
          id,
          namedValidator,
        }),
      },
      context,
      ...rest,
    );
    if (typeof result?.field !== 'object' || typeof result?.error !== 'object') {
      $logger.error(new TypeError('result should have error and field objects'));
    }
    return result;
  };
}

function getBasicValidatorProps ({
  id,
  namedValidator,
}: {
  id?: string
  namedValidator?: NamedValidator
}): BasicValidatorProps {
  return {
    field: {
      rowUniqueId: id,
      hasError: Boolean(namedValidator?.$errors.length),
      hasSuccess: Boolean(namedValidator?.$dirty && !namedValidator?.$invalid),
      isInvalid: namedValidator?.$invalid ?? false,
      isRequired: Boolean(namedValidator?.required),
      onBlur: () => {
        setTimeout(() => {
          namedValidator?.$touch();
        }, 200);
      },
    },
    error: {
      id,
      validator: namedValidator,
    },
  };
}
export function getFormRowErrorMessage ({ $awt }: { $awt: PostTranslationPluginProvides['awt'] }, rowValidator: BaseValidation | undefined, tokens: Record<string, string>) {
  const hardcodedBeTokens = new Set(['backend', 'password', 'taxNumberValidator', 'phoneValidator', 'groupIdNumberValidator', 'ucbAddressValidator', 'applicationIdent', 'contractNumber']);

  const DEFAULT_ERROR_MESSAGES: Record<string, string> = {
    email: 'aw.common.form.validation.email',
    required: 'aw.common.form.validation.required',
    sameAsPassword: 'aw.common.form.validation.same_password',
    notSameAsOldPassword: 'aw.common.form.validation.not_same_as_old_password',
    sameAsEmail: 'aw.common.form.validation.same_email',
    samePassword: 'aw.common.form.validation.same_password',
  };

  if (rowValidator) {
    for (const key in rowValidator) {
      if (key.startsWith('$')) {
        continue;
      }
      const row = rowValidator?.[key];
      if (!row?.$invalid) {
        continue;
      }
      const tokenValue = tokens[key];
      if (hasProperty(tokens, key)) {
        if (hardcodedBeTokens.has(key)) {
          // Backendről már lefordított hibaüzenet jön vissza és a backendről jövő validátorok már lefordítva jönnek vissza
          return tokenValue;
        }
        // if use key inside token, eg.: "hello {first_name}", then use 'Key' postfix in validator token name eg.: checkName -> checkNameKey
        const tokenKey = tokens[`${key}Key`];
        return $awt(tokenValue || '', tokenKey ? { [key]: tokenKey } : undefined);
      }
      const defaultMessage = DEFAULT_ERROR_MESSAGES[key];
      if (defaultMessage) {
        return $awt(defaultMessage);
      } else if (typeof rowValidator?.$errors?.[0]?.$message === 'string') {
        return rowValidator?.$errors?.[0]?.$message;
      } else {
        return 'undefined';
      }
    }
  }
}
export function withMessageForInvalid (msg: string, validator: ValidationRule) {
  return helpers.withMessage(
    ({ $invalid }) => $invalid ? msg : '',
    validator,
  );
};
